import React, { useContext, useEffect, useState } from "react";
import { Modal, Row, Col, Card, Table, Form, Select, Input } from "antd";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../context/AppContextProvider";
import {
  getSingleProductStock,
  getBranches,
} from "../../../../network/network";
import CurrencyFormatter from "../../../../components/CurrencyFormatter";
import { handleApiError } from "../../../../utils/errorHandler";

const StockOverviewModal = ({ visible, product, onClose }) => {
  const { appConfig } = useContext(AppContext);
  const { t } = useTranslation();

  const [branchesData, setBranchesData] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [branchesList, setBranchesList] = useState([]);
  const [searchParams, setSearchParams] = useState({});

  const [filterForm] = Form.useForm();

  const resetState = () => {
    setBranchesData([]);
    setBranchesLoading(false);
    setPagination({
      current: 1,
      pageSize: 5,
      total: 0,
    });
    setSearchParams({});
    filterForm.resetFields();
  };

  const fetchBranchesStock = (page = 1, pageSize = 10, extraParams = {}) => {
    if (!product?.id) return;
    setBranchesLoading(true);

    getSingleProductStock({
      product_id: product.id,
      page,
      per_page: pageSize,
      ...extraParams,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        const data = response.data?.data || {};
        const branches = data.branches_stock || [];
        const p = data.pagination || {};

        setBranchesData(branches);
        setPagination({
          current: p.current_page || page,
          pageSize: p.per_page || pageSize,
          total: p.total || branches.length || 0,
        });
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setBranchesLoading(false);
      });
  };

  useEffect(() => {
    if (!visible) return;
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesList(response.data.data.branches || []);
      })
      .catch((error) => handleApiError(error, t));
  }, [visible, t]);

  useEffect(() => {
    if (visible && product?.id) {
      fetchBranchesStock(1, pagination.pageSize, searchParams);
    }
    if (!visible) {
      resetState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, product?.id, JSON.stringify(searchParams)]);

  const handleTableChange = (page, pageSize) => {
    fetchBranchesStock(page, pageSize, searchParams);
  };

  const handleSearch = (values) => {
    let params = {};
    if (values.filter_by_branch) {
      params = {
        filter_by_branch: values.filter_by_branch,
      };
    }
    setSearchParams(params);
    fetchBranchesStock(1, pagination.pageSize, params);
  };

  const handleReset = () => {
    filterForm.resetFields();
    setSearchParams({});
    fetchBranchesStock(1, pagination.pageSize, {});
  };

  const columns = [
    {
      title: t("branch"),
      dataIndex: "branch_name",
    },
    {
      title: t("available_stock"),
      align: "center",
      dataIndex: "quantity",
      render: (quantity) => (
        <div
          style={{ color: Math.sign(quantity) === 1 ? "var(--color-success)" : "var(--color-danger)" }}
        >
          {quantity}
        </div>
      ),
    },
  ];

  return (
    <Modal
      open={visible}
      title={t("stock_overview")}
      onCancel={onClose}
      width={1000}
      footer={null}
      destroyOnClose
    >
      <div style={{ backgroundColor: "var(--color-bg)", padding: "20px" }}>
        {product && (
          <Row gutter={[20, 20]}>
            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={8}
              lg={8}
              xl={6}
              xxl={6}
            >
              <div
                style={{
                  textAlign: "center",
                  padding: "20px",
                  borderRadius: "0.5rem",
                  backgroundColor: "var(--color-white)",
                }}
              >
                <img
                  alt={product.name}
                  src={
                    product.image
                      ? product.image
                      : "/images/image-not-found.png"
                  }
                  style={{
                    width: "100%",
                    maxWidth: "195px",
                    height: "auto",
                  }}
                />
                <h3>
                  {appConfig.direction === "rtl"
                    ? product.name
                    : product.name_en}
                </h3>
                <p style={{ marginBottom: 0 }}>
                  {appConfig.direction === "ltr"
                    ? product.category
                    : product.category_ar}
                </p>
                <p style={{ color: "var(--color-primary)", marginBottom: 0 }}>
                  <CurrencyFormatter value={product.price} />
                </p>
              </div>
            </Col>

            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={16}
              lg={16}
              xl={18}
              xxl={18}
            >
              <Card>
                <Form
                  name="branch-filter-form"
                  layout="inline"
                  onFinish={handleSearch}
                  form={filterForm}
                  style={{
                    marginBottom: 16,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Form.Item name="filter_by_branch" label={t("branch")}>
                    <Select
                      showSearch
                      allowClear
                      style={{ width: 200 }}
                      placeholder={t("branch")}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={branchesList.map((branch) => ({
                        label: branch.name,
                        value: branch.name,
                      }))}
                    />
                  </Form.Item>

                  <Form.Item>
                    <Input
                      type="submit"
                      value={t("search")}
                      className="app-search-submit"
                    />
                  </Form.Item>
                  <Form.Item>
                    <Input
                      type="button"
                      value={t("reset")}
                      onClick={handleReset}
                    />
                  </Form.Item>
                </Form>

                <Table
                  columns={columns}
                  dataSource={branchesData.map((item, index) => ({
                    ...item,
                    key: item.id || index,
                  }))}
                  loading={branchesLoading}
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    onChange: handleTableChange,
                  }}
                />
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </Modal>
  );
};

export default StockOverviewModal;
